import { useEffect, useState } from "react";

import { Button, Grid, Paper, Stack } from "@mui/material";
import dayjs from "dayjs";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  getAllPromoCodes,
  removePromoCode,
} from "../../Api/Signin/Transaction.Api";
import { ReactComponent as PromoCode } from "../../Assets/Images/Icon/PromoCodeICON.svg";
import {
  Heading1,
  Heading3,
} from "../../Components/atoms/Typography/Heading/Heading";
import { Text } from "../../Components/atoms/Typography/Text/Text";
import { ActionButton } from "../../Components/molecules/ActionButton/ActionButton";
import { BasicGrid } from "../../Components/organisms/BasicGrid/BasicGrid";
import ConfirmDialog from "../../Components/organisms/ConfirmDialog/ConfirmDialog";
import { PromoCodeDrawer } from "../../Components/organisms/PromoCodeDrawer/PromoCodeDrawer";
import { ViewPromoDetails } from "../../Components/organisms/PromoCodeDrawer/ViewPromoDetails";
import { SelectInput } from "../../Components/atoms/Input/SelectInput";
import { Theme } from "../../Style/Theme";
import useOverlayState from "../../Utils/Hooks/useOverlayState";
import { getProviderDisplayName } from "../../Utils/WordFormatter/ProviderAlias";

export const Dashboard = () => {
  // ------------------------------------------------ Start => Deifinitions/ inits --------------------------------------------------

  const { t } = useTranslation();
  const [promoCodeDetails, setPromoCodeDetails] = useState([]);
  const { OverlayState, openOverlay, closeOverlay } = useOverlayState();
  const [pageSize, setPageSize] = useState(10);
  const [pageNumber, setPageNumber] = useState(1);
  const [rowData, setRowData] = useState();
  const [isUpdate, setIsUpdate] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [didFirstTimeRunPms, setDidFirstTimeRunPms] = useState(false);
  const columndata = [
    {
      fieldtext: t("common:table-field.promo-code"),
      field: "code",
      width: "180",
      textAlign: "Left",
      template: (props) => {
        return (
          <span
            style={{
              color: isPromoActive(props.createDate, props.expirationDate)
                ? "#37B003"
                : "red",
            }}
          >
            {props.code}
          </span>
        );
      },
    },
    {
      fieldtext: t("common:table-field.promo-type"),
      field: "type",
      width: "180",
      textAlign: "Left",
      template: (props) => {
        const type = props.type.toLowerCase();
        return t(`finance-add-promo-code.${type}`, { ns: "glossary" });
      },
    },
    {
      fieldtext: t("common:table-field.provider", { defaultValue: "Healthcare provider" }),
      field: "providerName",
      width: "190px",
      textAlign: "Left",
      template: (props) => {
        return props.providerName ? getProviderDisplayName(props.providerName) : "Universal";
      },
    },
    {
      fieldtext: t("common:table-field.reusability"),
      field: "type",
      width: "180",
      textAlign: "Left",
      template: (props) => {
        let reusability = props.reusability;
        return (
          <span>
            {reusability
              ? t("common:table-field.reusable")
              : t("common:table-field.non-reusable")}
          </span>
        );
      },
    },
    {
      fieldtext: t("common:table-field.value"),
      field: "value",
      width: "140px",
      textAlign: "LastConsultation",
      template: (props) => {
        let value =
          props.type == "Fixed" ? `Rs.${[props.value]}` : `${[props.value]}%`;
        return value;
      },
    },
    {
      fieldtext: t("common:table-field.validity"),
      field: "validity",
      width: "148px",
      textAlign: "Left",
      template: (props) => {
        const expirationDate = new Date(props.expirationDate);
        const startDate = new Date(props.startDate);
        const diffTime = Math.abs(expirationDate - startDate);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
        return `${diffDays} ${t("common:table-field.days")}`;
      },
    },
    {
      fieldtext: t("common:table-field.created-date"),
      field: "createDate",
      width: "176px",
      textAlign: "Left",
      enableGroupByFormat: true,
      template: (props) => {
        return dayjs(props.createDate).format("DD/MM/YYYY");
      },
    },
    {
      fieldtext: t("common:table-field.start-date"),
      field: "startDate",
      width: "176px",
      textAlign: "Left",
      enableGroupByFormat: true,
      template: (props) => {
        return dayjs(props?.startDate).format("DD/MM/YYYY");
      },
    },
    {
      fieldtext: t("common:table-field.expiration-date"),
      field: "expirationDate",
      width: "204px",
      textAlign: "Left",
      enableGroupByFormat: true,
      template: (props) => {
        return dayjs(props.expirationDate).format("DD/MM/YYYY");
      },
    },
    {
      fieldtext: t("common:table-field.actions"),
      field: "actions",
      width: "88px",
      textAlign: "Left",
      allowSorting: false,
      headerTextAlign: "Left",
      key: 6,
      template: (props) => {
        const activePromoCode = isPromoActive(
          props.createDate,
          props.expirationDate,
        );
        return (
          <ActionButton
            clickHandler={(e) => {
              handleActionClick(e, props);
            }}
            Options={
              activePromoCode
                ? [
                  {
                    id: 110,
                    element: (
                      <Text
                        onClick={() => {
                          setDrawerOpen(true);
                          setRowData(props);
                          setIsUpdate(true);
                        }}
                        text={t("common:action.edit")}
                        color={Theme.palette.blue.primaryBlue}
                      />
                    ),
                  },
                ]
                : [
                  {
                    id: 111,
                    element: (
                      <Text
                        onClick={() => {
                          openOverlay({
                            OverlayName: "view-record",
                            record: props,
                            mode: "view",
                          });
                        }}
                        text={t("common:action.view")}
                        color={Theme.palette.blue.primaryBlue}
                      />
                    ),
                  },
                  {
                    id: 112,
                    element: (
                      <Text
                        onClick={() => {
                          openOverlay({
                            OverlayName: "delete-record",
                            record: { id: props?.id },
                            mode: "delete",
                          });
                        }}
                        text={t("common:action.delete")}
                        color={Theme.palette.blue.primaryBlue}
                      />
                    ),
                  },
                ]
            }
          />
        );
      },
    },
  ];
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { watch, control } = useForm({
    defaultValues: { promoType: 0 },
    mode: "onChange",
  });
  const watchAll = watch();

  const filteredPromoCodes = promoCodeDetails?.filter((promo) => {
    if (watchAll.promoType === 1) return promo.isGeneral === true;
    if (watchAll.promoType === 0) return promo.isGeneral === false;
    return true;
  });

  const activeColumnData =
    watchAll.promoType === 1
      ? columndata.filter((col) => col.field !== "providerName")
      : columndata;

  const totalRecordCount = filteredPromoCodes?.length || 0;
  const pageData = {
    currentPage: pageNumber,
    pageCount: Math.ceil(totalRecordCount / pageSize),
    pageSize: pageSize,
    totalPages: Math.ceil(totalRecordCount / pageSize),
    totalRecordsCount: totalRecordCount,
  };
  
  const paginatedPromoCodes = filteredPromoCodes?.slice(
    (pageNumber - 1) * pageSize,
    pageNumber * pageSize
  );

  // ------------------------------------------------ End => Deifinitions/ inits ------------------------------------------------------

  // ---------------------------------------------- Start => Action functions ---------------------------------------------------------

  // Action click handler
  const handleActionClick = async (e, props) => {
    if (e == 110) {
      setDrawerOpen(true);
      setRowData(props);
      setIsUpdate(true);
    } else if (e == 111) {
      openOverlay({
        OverlayName: "view-record",
        record: props,
        mode: "view",
      });
    } else if (e == 112) {
      openOverlay({
        OverlayName: "delete-record",
        record: { id: props?.id },
        mode: "delete",
      });
    }
  };

  // Handle when changing the rows per page (page size)
  useEffect(() => {
    setPageNumber(1);
  }, [pageSize]);

  // Reset page number on filter change
  useEffect(() => {
    setPageNumber(1);
  }, [watchAll.promoType]);

  // Handle on page load
  useEffect(() => {
    GetPromoCodes();
    setDidFirstTimeRunPms(true);
  }, []);

  const handlePageSizeChange = (selectedPageSize) => {
    setPageSize(selectedPageSize);
  };

  const handlePaginationClick = (pageNumber) => {
    setPageNumber(pageNumber);
  };

  const isPromoActive = (startDate, endDate) => {
    const currentDate = new Date();
    const dateFormat = (date) => {
      const convertedDate = new Date(date);
      const year = convertedDate.getFullYear();
      const month = String(convertedDate.getMonth() + 1).padStart(2, "0");
      const day = String(convertedDate.getDate()).padStart(2, "0");
      let formattedDate = "";
      formattedDate = `${year}-${month}-${day}`;
      return formattedDate;
    };
    return (
      dateFormat(currentDate) == dateFormat(endDate) ||
      currentDate < new Date(endDate)
    );
  };

  // Get all promo codes
  const GetPromoCodes = async () => {
    try {
      setIsDataLoading(true);
      let response = await getAllPromoCodes(1, 10000);
      if (response.type === 0) {
        setPromoCodeDetails(response.data.promoCodes);
        setIsDataLoading(false);
      } else {
        setPromoCodeDetails([]);
        setIsDataLoading(false);
      }
    } catch (exception) {
      setIsDataLoading(false);
    }
  };

  // Remove promo code
  const RemovePromoCode = async (id) => {
    try {
      setIsDataLoading(true);
      let response = await removePromoCode(id);
      if (response.type === 0) {
        setPageNumber(1);
        setPageSize(10);
        GetPromoCodes();
      }
      setIsDataLoading(false);
    } catch (exception) {
      setIsDataLoading(false);
    }
  };

  // ------------------------------------------------- End => Action functions --------------------------------------------------------

  return (
    <>
      <Grid container direction={"column"}>
        <Grid item>
          <Stack
            direction={"row"}
            justifyContent={"space-between"}
            mb={2.8}
            sx={{ minHeight: "48px" }}
          >
            <Heading1 text={t("glossary:finance-dashboard.title")} />
            <Stack direction={"row"} spacing={2}>
              <SelectInput
                size={"small"}
                width={"220px"}
                height={"48px"}
                control={control}
                backgroundColor={"#FFF"}
                options={[
                  { label: "Exclusive repeat codes", key: 0 },
                  { label: "Universal repeat codes", key: 1 }
                ]}
                name={"promoType"}
                menuItemLabel={"label"}
                menuItemKey={"key"}
              />
              <Button
                disableTouchRipple
                variant="lightBlue"
                sx={{ borderRadius: "14px" }}
                onClick={() => {
                  setDrawerOpen(!drawerOpen);
                  setIsUpdate(false);
                }}
              >
                <PromoCode style={{ marginRight: "10px" }} fill="white" />
                <Heading3
                  text={t("glossary:finance-dashboard.action.add-promo-code")}
                  color="#FFFFFF"
                />
              </Button>
            </Stack>
          </Stack>
        </Grid>
        <Grid item width="100%">
          <>
            <Paper
              sx={{
                padding: "20px 24px",
                boxShadow: "0px 0px 25px 0px rgba(0, 0, 0, 0.05)",
                position: "relative",
                height: "100%",
              }}
            >
              <div>
                <BasicGrid
                  data={paginatedPromoCodes}
                  isLoading={isDataLoading}
                  columnArray={activeColumnData}
                  allowSorting
                  allowPaging
                  rowSelect={false}
                  customSearchText={watchAll.searchText}
                  field={[]}
                  pageData={pageData}
                  paginationClick={handlePaginationClick}
                  pageSizeChange={handlePageSizeChange}
                />
              </div>
            </Paper>
          </>
        </Grid>
      </Grid>
      {drawerOpen && (
        <PromoCodeDrawer
          OpenDrawer={drawerOpen}
          close={() => setDrawerOpen(!drawerOpen)}
          isUpdate={isUpdate}
          data={rowData}
          refetch={() => {
            GetPromoCodes();
          }}
        />
      )}
      {OverlayState.visible && OverlayState.OverlayName === "delete-record" && (
        <ConfirmDialog
          open={OverlayState.visible}
          close={closeOverlay}
          title={t("glossary:finance-dashboard.action.delete-promo-code")}
          description={t("common:alert-confirm.proceed-confirm-delete")}
          onOkClick={() => {
            RemovePromoCode(OverlayState?.record?.id);
            closeOverlay();
          }}
          onOkTitle={t("common:alert-confirm.yes")}
          onCancelClick={closeOverlay}
          onCancelTitle={t("common:alert-confirm.no")}
        />
      )}
      {OverlayState.visible && OverlayState.OverlayName === "view-record" && (
        <ViewPromoDetails OpenDrawer={OverlayState.visible} record={OverlayState.record} close={closeOverlay} />
      )}
    </>
  );
};
